import {Button, CounterLabel, Label, Spinner, Tooltip} from '@primer/react'
import {AutomatedReviewDiffEntry} from './AutomatedReviewDiffEntry'
import React, {useState, useRef, useCallback, useMemo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ApplySuggestionDialog} from '../CodeSuggestionActions/ApplySuggestionDialog'
import {useApplyAutomatedSuggestionMutation} from '@github-ui/pull-requests/use-apply-automated-suggestion-mutation'
import type {AutomatedReviewSuggestionProps} from './AutomatedReviewSuggestion'
import {FoldIcon, UnfoldIcon} from '@primer/octicons-react'
import type {Suggestion, SuggestedChangeLineRange} from '../../../conversation-types'
import styles from './AutomatedReviewSuggestionStates.module.css'
import {
  useSuggestionInBatch,
  useSuggestionBatchActions,
  useIsSuggestionBatchPending,
  useSuggestionBatch,
  usePullRequestBasePath,
  useComparisonInfo,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {saveActiveBatchToStorage} from '@github-ui/pull-requests/helpers/suggestion-batch-storage'
import {
  getLineRangesFromDiffEntries,
  validateSuggestedChangeCanBeAddedToBatch,
} from '../../../helpers/suggested-changes'
import {CodeSuggestionUnavailable} from '../CodeSuggestionActions/CodeSuggestionUnavailable'

interface AutomatedReviewSuggestionBaseProps {
  suggestion: Suggestion
  actions?: React.ReactNode
}

function AutomatedReviewSuggestionBase({suggestion, actions}: AutomatedReviewSuggestionBaseProps) {
  const diffEntries = suggestion.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0

  return (
    <div
      className={`d-flex flex-column color-fg-default ${styles.suggestion}`}
      data-testid="automated-review-suggestion"
    >
      {/* Metadata */}
      {suggestedChangesCount > 0 && (
        <div className="d-flex flex-items-center flex-justify-between text-small color-fg-muted pt-2">
          <div>
            Suggested changeset <CounterLabel scheme="secondary">{suggestedChangesCount}</CounterLabel>
          </div>
        </div>
      )}

      {/* Diffs */}
      {diffEntries.map(diffEntry => {
        return <AutomatedReviewDiffEntry key={diffEntry.path} diffEntry={diffEntry} />
      })}

      {actions}
    </div>
  )
}

export function AutomatedReviewSuggestionPending(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionPending} data-testid="automated-review-suggestion-pending">
      <Spinner size="medium" />
      Loading suggestion...
    </div>
  )
}

// TODO(@aliceclv): we store the suggestion_error_message in the DB, we can display it here
export function AutomatedReviewSuggestionError(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionError} data-testid="automated-review-suggestion-error">
      <h3 className={styles.suggestionErrorTitle}>Sorry, we could not generate an autofix suggestion.</h3>
      <p className="color-fg-muted">
        Try pushing a new commit or if the problem persists{' '}
        <a className="fgColor-link" href="https://support.github.com/">
          <u>contact support</u>
        </a>
        .
      </p>
    </div>
  )
}

export function AutomatedReviewSuggestionOutdated({suggestion, isClassicExperience}: AutomatedReviewSuggestionProps) {
  const Actions = (
    <div className="d-flex flex-items-center flex-justify-between gap-1">
      <span className="color-fg-muted text-small">Unable to commit as this autofix suggestion is now outdated.</span>

      {isClassicExperience ? (
        <Button size="small" variant="primary" disabled>
          Commit suggestion
        </Button>
      ) : (
        <Tooltip text="This autofix suggestion is outdated">
          <Button variant="default" inactive>
            Apply suggestion
          </Button>
        </Tooltip>
      )}
    </div>
  )
  return suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} actions={Actions} />
}

export function AutomatedReviewSuggestionApplied({suggestion}: AutomatedReviewSuggestionProps) {
  const [isFolded, setIsFolded] = useState(true)
  const fold = () => setIsFolded(!isFolded)

  return (
    <div className={styles.suggestionApplied}>
      <div className="d-flex flex-justify-between flex-items-center">
        <span className={styles.suggestionAppliedText}>This autofix suggestion was applied.</span>
        <Button
          variant="invisible"
          onClick={fold}
          leadingVisual={isFolded ? UnfoldIcon : FoldIcon}
          size="small"
          className="color-fg-muted"
        >
          {isFolded ? 'Show' : 'Hide'} autofix suggestion
        </Button>
      </div>
      {!isFolded && suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} />}
    </div>
  )
}

export function AutomatedReviewSuggestionPresent({
  automatedCommentId,
  basePath,
  commentAuthorLogin,
  commentDatabaseId,
  commentTitle,
  isClassicExperience,
  suggestion,
}: AutomatedReviewSuggestionProps) {
  const [isApplySuggestionDialogOpen, setIsApplySuggestionDialogOpen] = useState(false)
  const openDialog = useCallback(() => setIsApplySuggestionDialogOpen(true), [])
  const closeDialog = useCallback(() => setIsApplySuggestionDialogOpen(false), [])

  const diffEntries = suggestion?.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0
  const applyButtonRef = useRef<HTMLButtonElement>(null)
  // Note(@aliceclv): this is optimized for Universe ship & code quality
  // We should have a default commit message passed from the backend when adapting the component to code scanning & CCR
  const defaultCommitMessage = `Potential fix for pull request finding${commentTitle ? ` '${commentTitle}'` : ''}`
  const {mutate: applyAutomatedSuggestion} = useApplyAutomatedSuggestionMutation(automatedCommentId, basePath)

  const handleCommitSuggestion = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      applyAutomatedSuggestion(
        {message: commitMessage},
        {
          onSuccess: () => {
            closeDialog()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'applyAutomatedSuggestion', 'Failed to commit suggested changes')
          },
        },
      )
    },
    [applyAutomatedSuggestion, closeDialog],
  )

  if (!suggestion) return null

  const actions = (
    <>
      {isClassicExperience ? (
        <RailsExperienceButtons applyButtonRef={applyButtonRef} onApplyButtonClick={openDialog} />
      ) : (
        // commentDatabaseId shouldn't ever be null here, but we add this check to satisfy TypeScript
        commentDatabaseId != null && (
          <ReactExperienceButtons
            commentDatabaseId={commentDatabaseId}
            applyButtonRef={applyButtonRef}
            onApplyButtonClick={openDialog}
            suggestion={suggestion}
          />
        )
      )}

      {isApplySuggestionDialogOpen && suggestedChangesCount > 0 && (
        <ApplySuggestionDialog
          authorLogins={commentAuthorLogin ? [commentAuthorLogin] : []}
          batchSize={suggestedChangesCount}
          defaultCommitMessage={defaultCommitMessage}
          onClose={closeDialog}
          onCommit={handleCommitSuggestion}
          dialogTitle={isClassicExperience ? 'Commit suggestion' : 'Apply suggestion'}
          returnFocusRef={applyButtonRef}
        />
      )}
    </>
  )

  return <AutomatedReviewSuggestionBase suggestion={suggestion} actions={actions} />
}

function isBatchButtonEnabled() {
  return isFeatureEnabled('batch_suggested_changes') && isFeatureEnabled('copilot_code_review_batch_apply_suggestions')
}

function RailsExperienceButtons({
  applyButtonRef,
  onApplyButtonClick,
}: {
  applyButtonRef: React.RefObject<HTMLButtonElement | null>
  onApplyButtonClick: () => void
}) {
  const showBatchButton = isBatchButtonEnabled()

  return (
    <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
      <Button
        data-testid="commit-suggestion-button"
        size="small"
        variant="primary"
        ref={applyButtonRef}
        onClick={onApplyButtonClick}
      >
        Commit suggestion
      </Button>
      {showBatchButton && (
        <Tooltip text="Batching autofix suggestions is only available in the new Files changed experience">
          <Button data-testid="add-suggestion-to-batch-button" size="small" inactive>
            Add suggestion to batch
          </Button>
        </Tooltip>
      )}
    </div>
  )
}

function ReactExperienceButtons({
  commentDatabaseId,
  applyButtonRef,
  onApplyButtonClick,
  suggestion,
}: {
  commentDatabaseId: number
  applyButtonRef: React.RefObject<HTMLButtonElement | null>
  onApplyButtonClick: () => void
  suggestion: Suggestion | undefined
}) {
  const showApplyButton = !useIsSuggestionBatchPending()
  const showBatchButton = isBatchButtonEnabled() && commentDatabaseId != null

  const suggestionId = useMemo(() => ({commentDatabaseId, suggestionIndex: undefined}), [commentDatabaseId])
  const isPendingInBatch = useSuggestionInBatch(suggestionId)

  const suggestionBatch = useSuggestionBatch()
  const pathLineRanges = useMemo(
    () => getLineRangesFromDiffEntries(suggestion?.diffEntries || []),
    [suggestion?.diffEntries],
  )
  const {isValid: canAddToBatch, reason} = useMemo(
    () => validateSuggestedChangeCanBeAddedToBatch({pathLineRanges}, suggestionBatch.activeBatch),
    [pathLineRanges, suggestionBatch.activeBatch],
  )

  const canAddToOrRemoveFromBatch = isPendingInBatch || canAddToBatch
  return (
    <>
      {showBatchButton && !canAddToOrRemoveFromBatch && (
        <CodeSuggestionUnavailable
          reason={reason ?? 'Unable to commit due to other pending changes affecting this line.'}
          className="m-0"
        />
      )}
      <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
        {showApplyButton && (
          <Button
            data-testid="commit-suggestion-button"
            variant="default"
            ref={applyButtonRef}
            onClick={onApplyButtonClick}
          >
            Apply suggestion
          </Button>
        )}
        {showBatchButton && canAddToOrRemoveFromBatch && (
          <BatchButton commentDatabaseId={commentDatabaseId} pathLineRanges={pathLineRanges} />
        )}
      </div>
    </>
  )
}

function BatchButton({
  commentDatabaseId,
  pathLineRanges,
}: {
  commentDatabaseId: number
  pathLineRanges: Array<{path: string; lineRange: SuggestedChangeLineRange}>
}) {
  const suggestionId = useMemo(() => ({commentDatabaseId, suggestionIndex: undefined}), [commentDatabaseId])
  const isPendingInBatch = useSuggestionInBatch(suggestionId)

  const suggestionBatch = useSuggestionBatch()
  const {addSuggestionToBatch, removeSuggestionFromBatch} = useSuggestionBatchActions()

  const pullRequestBasePath = usePullRequestBasePath()
  const comparisonInfo = useComparisonInfo()

  const batchedSuggestion = useMemo(() => ({...suggestionId, pathLineRanges}), [suggestionId, pathLineRanges])

  const handleAddToBatch = useCallback(() => {
    addSuggestionToBatch(batchedSuggestion)
    saveActiveBatchToStorage(pullRequestBasePath, comparisonInfo.fullDiff.headOid, [
      ...suggestionBatch.activeBatch,
      batchedSuggestion,
    ])
  }, [addSuggestionToBatch, batchedSuggestion, suggestionBatch, pullRequestBasePath, comparisonInfo])

  const handleRemoveFromBatch = useCallback(() => {
    removeSuggestionFromBatch(suggestionId)
    saveActiveBatchToStorage(
      pullRequestBasePath,
      comparisonInfo.fullDiff.headOid,
      suggestionBatch.activeBatch.filter(item => item.commentDatabaseId !== commentDatabaseId),
    )
  }, [removeSuggestionFromBatch, suggestionId, commentDatabaseId, suggestionBatch, pullRequestBasePath, comparisonInfo])

  if (isPendingInBatch) {
    return (
      <div className="d-flex flex-items-center flex-justify-between flex-1">
        <Label variant="attention">Pending in batch</Label>
        <Button data-testid="remove-suggestion-from-batch-button" variant="danger" onClick={handleRemoveFromBatch}>
          Remove from batch
        </Button>
      </div>
    )
  }

  return (
    <Button data-testid="add-suggestion-to-batch-button" variant="primary" onClick={handleAddToBatch}>
      Add suggestion to batch
    </Button>
  )
}
